/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useCallback, useContext, useEffect, useState, useSyncExternalStore} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {assertNever} from '@Utils/assert-never.util';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {duplicateSelectedComponent, removeSelectedComponents, toggleComponentLock} from '@EmailMakerReact/lib/email-maker-clipboard-library';
import {setDefaultSelection} from '@EmailMakerReact/lib/email-maker-selection-library';
import {saveUserComponentTemplate} from '@EmailMakerReact/lib/email-maker-section-template-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {ICON_BUTTON_CONTROL_ACTIONS, ICON_BUTTON_RUNTIME_ICONS, ICON_BUTTON_RUNTIME_LABEL_KEYS, SaveButtonState} from './icon-button-control.types';
import type {ComputeIconClassParams, ComputeLabelKeyParams, IconButtonActionState, IconButtonControlDescriptor} from './icon-button-control.types';
import styles from './icon-button-control.module.scss';

/** All controls built by makeIconButtonControl — used by renderSector to group them into one toolbar row. */
export const ICON_BUTTON_CONTROL_SET = new WeakSet<EmailMakerControl>();

/** Descriptor for each icon-button control — used by the mobile strip to drive the shared hook. */
export const ICON_BUTTON_CONTROL_DESCRIPTORS = new WeakMap<EmailMakerControl, IconButtonControlDescriptor>();

interface IconButtonControlInnerProps extends EmailMakerControlProps {
  descriptor: IconButtonControlDescriptor;
}

function useLockState(component: EmailMakerControlProps['component']): boolean {
  return useSyncExternalStore(
    useCallback(
      (onChange: () => void): (() => void) => {
        component.on('change:draggable', onChange);
        return (): void => {
          component.off('change:draggable', onChange);
        };
      },
      [component]
    ),
    useCallback((): boolean => {
      return Boolean(component.get('draggable'));
    }, [component])
  );
}

/** Shared render-state + click handler for one icon-button action, consumed by both the desktop control and the mobile strip. */
export function useIconButtonAction(component: EmailMakerControlProps['component'], descriptor: IconButtonControlDescriptor): IconButtonActionState {
  const {editor} = useContext(emailMakerContext);
  const [saveState, setSaveState] = useState<SaveButtonState>(SaveButtonState.IDLE);

  // This instance is reused across selections; reset transient save state so a stale "Saved" doesn't carry over.
  useEffect((): void => {
    setSaveState(SaveButtonState.IDLE);
  }, [component]);

  const isDraggable = useLockState(component);
  const isLockButton = descriptor.action === ICON_BUTTON_CONTROL_ACTIONS.LOCK;
  const isSaveButton = descriptor.action === ICON_BUTTON_CONTROL_ACTIONS.SAVE;
  const isDeleteButton = descriptor.action === ICON_BUTTON_CONTROL_ACTIONS.DELETE;
  const isClearSelection = descriptor.action === ICON_BUTTON_CONTROL_ACTIONS.CLEAR_SELECTION;
  const isSaveBusy = isSaveButton && saveState !== SaveButtonState.IDLE;

  const iconClass = computeIconClass({descriptor, isLockButton, isDraggable, isSaveButton, saveState});
  const labelKey = computeLabelKey({descriptor, isLockButton, isDraggable});
  const label = window.i18next.t(labelKey);
  const captionText = computeCaptionText(label, isSaveButton, saveState);

  const handleClick = useCallback((): void => {
    if (!editor) {
      return;
    }

    switch (descriptor.action) {
      case ICON_BUTTON_CONTROL_ACTIONS.DUPLICATE:
        void duplicateSelectedComponent(editor);
        break;
      case ICON_BUTTON_CONTROL_ACTIONS.DELETE:
        removeSelectedComponents(editor);
        break;
      case ICON_BUTTON_CONTROL_ACTIONS.LOCK:
        toggleComponentLock(editor);
        break;
      case ICON_BUTTON_CONTROL_ACTIONS.SAVE:
        if (saveState !== SaveButtonState.IDLE) {
          return;
        }
        setSaveState(SaveButtonState.SAVING);
        saveUserComponentTemplate(editor, {
          component,
          callback: (): void => {
            setSaveState(SaveButtonState.SAVED);
          },
        });
        break;
      case ICON_BUTTON_CONTROL_ACTIONS.CLEAR_SELECTION:
        setDefaultSelection(editor);
        break;
      default:
        assertNever(descriptor.action);
    }
  }, [editor, component, descriptor.action, saveState]);

  return {iconClass, label, captionText, handleClick, isDeleteButton, isClearSelection, isSaveBusy};
}

function IconButtonControlInner({component, descriptor}: IconButtonControlInnerProps): ReactElement {
  const {iconClass, label, captionText, handleClick, isDeleteButton, isClearSelection, isSaveBusy} = useIconButtonAction(component, descriptor);

  const buttonClassName = [styles.button, isDeleteButton ? styles.deleteColor : '', isSaveBusy ? styles.selected : ''].filter(Boolean).join(' ');

  return (
    <button aria-label={label} className={buttonClassName} onClick={handleClick} title={label} type="button">
      {renderIcon(iconClass)}
      {renderCaption(captionText, isClearSelection)}
    </button>
  );
}

function renderIcon(iconClass: string): ReactElement {
  return <Icon aria-hidden className={styles.icon} icon={iconClass} size={IconSize.SIZE_ICON_24} />;
}

function computeLabelKey({descriptor, isLockButton, isDraggable}: ComputeLabelKeyParams): string {
  if (!isLockButton) {
    return descriptor.labelKey;
  }
  return isDraggable ? ICON_BUTTON_RUNTIME_LABEL_KEYS.LOCK : ICON_BUTTON_RUNTIME_LABEL_KEYS.UNLOCK;
}

function computeIconClass({descriptor, isLockButton, isDraggable, isSaveButton, saveState}: ComputeIconClassParams): string {
  if (isLockButton) {
    return isDraggable ? ICON_BUTTON_RUNTIME_ICONS.LOCK : ICON_BUTTON_RUNTIME_ICONS.UNLOCK;
  }
  if (isSaveButton) {
    if (saveState === SaveButtonState.SAVING) {
      return ICON_BUTTON_RUNTIME_ICONS.SAVING;
    }
    if (saveState === SaveButtonState.SAVED) {
      return ICON_BUTTON_RUNTIME_ICONS.SAVED;
    }
  }
  return descriptor.iconClass;
}

/** Save swaps its caption to Saving/Saved while busy; every other button shows its plain label. */
function computeCaptionText(label: string, isSaveButton: boolean, saveState: SaveButtonState): string {
  if (isSaveButton && saveState === SaveButtonState.SAVING) {
    return window.i18next.t(ICON_BUTTON_RUNTIME_LABEL_KEYS.SAVING);
  }
  if (isSaveButton && saveState === SaveButtonState.SAVED) {
    return window.i18next.t(ICON_BUTTON_RUNTIME_LABEL_KEYS.SAVED);
  }
  return label;
}

/** Only the clear-selection caption truncates with ellipsis; every other label wraps in full. */
function renderCaption(captionText: string, isClearSelection: boolean): ReactElement {
  const captionClassName = isClearSelection ? `${styles.caption} ${styles.clearSelectionCaption}` : styles.caption;
  return <Text val={captionText} size={TextSize.SM} className={captionClassName} />;
}

export function makeIconButtonControl(descriptor: IconButtonControlDescriptor): EmailMakerControl {
  function IconButtonControl(props: EmailMakerControlProps): ReactElement {
    return <IconButtonControlInner {...props} descriptor={descriptor} />;
  }
  ICON_BUTTON_CONTROL_SET.add(IconButtonControl);
  ICON_BUTTON_CONTROL_DESCRIPTORS.set(IconButtonControl, descriptor);
  return IconButtonControl;
}
